/**
 * <product-form>
 *
 * Wraps the <form action="/cart/add"> on PDPs. Intercepts submit,
 * calls cartStore.add(), dispatches cart:item-added on success,
 * renders inline error Banner on failure.
 *
 * Listens for:
 *   document: variant:changed   - updates hidden variant id, label, running total.
 *   element : qty-stepper:change - updates quantity and running total.
 *
 * Dispatched events (on document):
 *   cart:item-added  { item }   On successful add.
 */

const TOAST_AUTO_DISMISS_MS = 2200;

class ProductForm extends HTMLElement {
  constructor() {
    super();
    this._sectionId = '';
    this._productId = '';
    this._form = null;
    this._variantInput = null;
    this._quantityInput = null;
    this._atcButton = null;
    this._atcLabel = null;
    this._atcTotal = null;
    this._errorSlot = null;
    this._currentVariant = null;
    this._quantity = 1;
    this._toast = null;
    this._toastTimer = null;
  }

  connectedCallback() {
    this._sectionId = this.getAttribute('data-section-id') || '';
    this._productId = this.getAttribute('data-product-id') || '';
    this._form = this.querySelector('form');
    if (!this._form) return;
    this._variantInput = this.querySelector('[data-variant-id]');
    // Find the quantity input. Prefer an explicit [data-quantity-input] hook if
    // present; otherwise read the qty-stepper's inner input (Phase 2 snippet).
    this._quantityInput = this.querySelector('[data-quantity-input]')
      || this.querySelector('qty-stepper input[type="number"]');
    this._atcButton = this.querySelector('[data-atc-button]');
    this._atcLabel = this.querySelector('[data-atc-label]');
    this._atcTotal = this.querySelector('[data-atc-total]');
    this._errorSlot = this.querySelector('[data-error-slot]');

    // Store bound handlers so disconnectedCallback can remove them.
    this._boundOnSubmit = this._onSubmit.bind(this);
    this._boundOnVariantChanged = this._onVariantChanged.bind(this);
    this._boundOnQtyChanged = this._onQtyChanged.bind(this);
    this._boundOnCartUpdated = this._onCartUpdated.bind(this);

    this._form.addEventListener('submit', this._boundOnSubmit);
    document.addEventListener('variant:changed', this._boundOnVariantChanged);
    this.addEventListener('qty-stepper:change', this._boundOnQtyChanged);
    document.addEventListener('cart:updated', this._boundOnCartUpdated);

    // Initial state: read variant id from hidden input (set by Liquid).
    // Then look up the variant's price + availability from the sibling
    // <variant-picker>'s product JSON so the running total renders correctly
    // before any variant:changed event fires (avoids race with variant-picker
    // dispatch timing).
    const initialVariantId = parseInt(this._variantInput && this._variantInput.value, 10);
    if (initialVariantId) {
      this._currentVariant = this._readVariantSummary(initialVariantId)
        || { id: initialVariantId, available: true, price: 0 };
      this._applyInventoryCap();
      this._refresh();
    }
  }

  /**
   * Read the variant's id/available/price/inventory fields from the sibling
   * variant-picker's product JSON. Used both at init time and on
   * variant:changed (since the variant:changed event payload omits the
   * inventory fields we need for the cart-aware cap).
   */
  _readVariantSummary(variantId) {
    const sectionSelector = this._sectionId ? `[data-section-id="${this._sectionId}"]` : '';
    const picker = document.querySelector(`variant-picker${sectionSelector}`);
    if (!picker) return null;
    const jsonNode = picker.querySelector('script[data-product-json]');
    if (!jsonNode) return null;
    try {
      const product = JSON.parse(jsonNode.textContent);
      const v = (product.variants || []).find(x => x.id === variantId);
      if (!v) return null;
      return {
        id: v.id,
        available: v.available !== false,
        price: v.price,
        inventory_quantity: v.inventory_quantity,
        inventory_management: v.inventory_management,
        inventory_policy: v.inventory_policy,
      };
    } catch (_) {
      return null;
    }
  }

  disconnectedCallback() {
    if (this._form && this._boundOnSubmit) {
      this._form.removeEventListener('submit', this._boundOnSubmit);
    }
    if (this._boundOnQtyChanged) {
      this.removeEventListener('qty-stepper:change', this._boundOnQtyChanged);
    }
    if (this._boundOnVariantChanged) {
      document.removeEventListener('variant:changed', this._boundOnVariantChanged);
    }
    if (this._boundOnCartUpdated) {
      document.removeEventListener('cart:updated', this._boundOnCartUpdated);
    }
    if (this._toastTimer) clearTimeout(this._toastTimer);
  }

  _onVariantChanged(event) {
    const detail = event.detail || {};
    if (detail.sectionId && detail.sectionId !== this._sectionId) return;
    // Re-read the full variant from the product JSON so inventory fields land
    // alongside the dispatched price/availability. The variant:changed event
    // intentionally carries a slim payload; the inventory cap needs more.
    const full = this._readVariantSummary(detail.variantId);
    this._currentVariant = full || {
      id: detail.variantId,
      available: detail.available !== false,
      price: detail.price,
    };
    if (this._variantInput) {
      this._variantInput.value = String(detail.variantId);
    }
    this._applyInventoryCap();
    this._refresh();
  }

  /**
   * Recompute the qty stepper's max from (variant.inventory_quantity - qty
   * already in the live cart for this variant). Without this, /cart/add.js
   * silently accepts orders past inventory (Shopify's single-shape POST is
   * lenient on inventory) which then 422s when the cart drawer's qty
   * stepper tries to go further via /cart/change.js (strict). This brings
   * PDP in line with the drawer's strict behavior.
   */
  _applyInventoryCap() {
    if (!this._currentVariant || !this._quantityInput) return;
    const tracked = this._currentVariant.inventory_management === 'shopify'
      && this._currentVariant.inventory_policy === 'deny';
    if (!tracked) {
      this._effectiveMax = null;
      return;
    }
    const inv = Number(this._currentVariant.inventory_quantity);
    if (!Number.isFinite(inv)) {
      this._effectiveMax = null;
      return;
    }
    let inCart = 0;
    if (window.cartStore && typeof window.cartStore.getCached === 'function') {
      const cart = window.cartStore.getCached();
      if (cart && Array.isArray(cart.items)) {
        for (let i = 0; i < cart.items.length; i++) {
          if (String(cart.items[i].variant_id) === String(this._currentVariant.id)) {
            inCart += Number(cart.items[i].quantity) || 0;
          }
        }
      }
    }
    const effectiveMax = Math.max(0, inv - inCart);
    this._effectiveMax = effectiveMax;
    // Sync the qty input + qty-stepper internals so +/- can't exceed the
    // effective max. Floor at 1 so the displayed value never reads "0";
    // the Add-to-bag CTA is disabled separately in _refresh when max<=0.
    const stepperMax = Math.max(1, effectiveMax);
    this._quantityInput.setAttribute('max', String(stepperMax));
    const stepper = this._quantityInput.closest('qty-stepper');
    if (stepper) {
      stepper.dataset.max = String(stepperMax);
      if (typeof stepper.max === 'number') stepper.max = stepperMax;
    }
    let currentQty = parseInt(this._quantityInput.value, 10);
    if (!Number.isFinite(currentQty) || currentQty < 1) currentQty = 1;
    if (currentQty > stepperMax) {
      this._quantityInput.value = String(stepperMax);
      this._quantity = stepperMax;
    }
    if (stepper && typeof stepper._syncDisabledState === 'function') {
      stepper._syncDisabledState();
    }
  }

  _onCartUpdated() {
    // Cart contents changed (qty edit, item add elsewhere, etc.) - the
    // amount left to add for this variant may have shifted, so recompute.
    this._applyInventoryCap();
    this._refresh();
  }

  _onQtyChanged(event) {
    const detail = event.detail || {};
    if (typeof detail.value === 'number') {
      this._quantity = detail.value;
      if (this._quantityInput) {
        this._quantityInput.value = String(detail.value);
      }
      this._refresh();
    }
  }

  _refresh() {
    if (!this._atcButton) return;
    // Archived products (orphan-archived collection) short-circuit to the
    // Sold Out CTA regardless of variant availability. The Liquid sets
    // data-force-sold-out="true" on <product-form>; variant switches must
    // not restore the Add-to-bag state.
    if (this.dataset.forceSoldOut === 'true') {
      this._setButtonState('sold-out', this._atcButton.dataset.labelSoldOut || 'Sold out');
      return;
    }
    if (!this._currentVariant) {
      this._setButtonState('select', this._atcButton.dataset.labelSelect || 'Select a size');
      return;
    }
    if (!this._currentVariant.available) {
      this._setButtonState('sold-out', this._atcButton.dataset.labelSoldOut || 'Sold out');
      return;
    }
    // Treat "all available units already in cart" as sold-out for the CTA -
    // adding more would 422 on the next /cart/change.js so blocking here is
    // honest to the customer.
    if (this._effectiveMax === 0) {
      this._setButtonState('sold-out', this._atcButton.dataset.labelSoldOut || 'Sold out');
      return;
    }
    const total = (this._currentVariant.price || 0) * this._quantity;
    const totalFormatted = this._atcButton.dataset.formatTotal
      ? this._atcButton.dataset.formatTotal.replace('{total}', this._formatMoney(total))
      : this._formatMoney(total);
    this._setButtonState('add', this._atcButton.dataset.labelAdd || 'Add to bag', totalFormatted);
  }

  _setButtonState(state, label, total) {
    if (!this._atcButton) return;
    this._atcButton.dataset.state = state;
    if (state === 'add') {
      this._atcButton.removeAttribute('disabled');
      this._atcButton.setAttribute('aria-busy', 'false');
    } else {
      this._atcButton.setAttribute('disabled', '');
    }
    if (this._atcLabel) this._atcLabel.textContent = label;
    if (this._atcTotal) this._atcTotal.textContent = (state === 'add' && total) ? total : '';
  }

  _formatMoney(cents) {
    // Format as PHP. Shopify locale is en-PH; the cart-side will use the proper money filter
    // but the running-total display is best-effort client-side.
    const amount = (cents / 100).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return '\u20B1' + amount;
  }

  async _onSubmit(event) {
    event.preventDefault();
    if (this.dataset.forceSoldOut === 'true') return;
    if (!this._currentVariant || !this._currentVariant.available) return;
    if (!window.cartStore || typeof window.cartStore.add !== 'function') {
      this._showError({ code: 'generic', message: 'Cart is loading. Please try again in a moment.' });
      return;
    }

    this._clearError();
    this._atcButton.setAttribute('aria-busy', 'true');
    this._atcButton.setAttribute('disabled', '');

    try {
      const formData = new FormData(this._form);
      const lineItem = await window.cartStore.add(formData);
      this._dispatchItemAdded(lineItem);
      this._showToast(lineItem);
    } catch (err) {
      this._showError(err);
    } finally {
      this._atcButton.setAttribute('aria-busy', 'false');
      this._refresh();
    }
  }

  _dispatchItemAdded(item) {
    document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { item }, bubbles: false }));
  }

  _showToast(item) {
    if (this._toast) {
      this._toast.remove();
      this._toast = null;
    }
    if (this._toastTimer) clearTimeout(this._toastTimer);

    const toast = document.createElement('div');
    toast.className = 'product-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.innerHTML = `<div class="banner banner--success"><span class="banner__body">${this._escapeHtml(this._atcButton.dataset.toastTemplate || `${item.product_title || 'Item'} added to your bag`)}</span></div>`;
    document.body.appendChild(toast);
    // Force a reflow before adding data-visible to ensure the transition runs.
    void toast.offsetHeight;
    toast.setAttribute('data-visible', 'true');

    this._toast = toast;
    this._toastTimer = setTimeout(() => {
      toast.removeAttribute('data-visible');
      setTimeout(() => { toast.remove(); }, 300);
      this._toast = null;
      this._toastTimer = null;
    }, TOAST_AUTO_DISMISS_MS);
  }

  _showError(err) {
    if (!this._errorSlot) return;
    const code = err.code || 'generic';
    const friendlyMap = {
      'sold_out': 'This product is sold out.',
      'unavailable': 'This product is no longer available. Please reload the page.',
      'queue_aborted': 'Previous cart update failed. Please try again.',
      'cart_fetch_failed': "Couldn't reach the cart. Please try again.",
      'add_failed': 'Could not add to bag. Please try again.',
      'generic': 'Something went wrong. Please try again.',
    };
    // Prefer Shopify's own description if it looks customer-acceptable (short, no technical prefix).
    let message = friendlyMap[code] || friendlyMap.generic;
    if (err.message && err.message.length < 200 && !err.message.startsWith('TypeError') && !err.message.startsWith('SyntaxError')) {
      message = err.message;
    }
    this._errorSlot.innerHTML = `<div class="banner banner--error product-form__error" role="alert"><span class="banner__body">${this._escapeHtml(message)}</span></div>`;
  }

  _clearError() {
    if (this._errorSlot) this._errorSlot.innerHTML = '';
  }

  _escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductForm);
}